import React from 'react';
import { getBookedHomeVisit } from '../../utils/homeVisitWorkflowUtils';

/**
 * BookedHomeVisitSection - Visar information om bokat hembesök
 * Accordion som automatiskt döljs efter hembesöket har passerat
 * 
 * ✅ Stödjer både nya workflow-strukturen och gamla strukturen (bakåtkompatibelt)
 * 
 * @param {Object} procurementData - Procurement-data med hembesöksinformation
 * @param {boolean} expanded - Om sektionen är expanderad
 * @param {Function} onToggle - Callback för att toggla expansion
 */
const BookedHomeVisitSection = ({ procurementData, expanded, onToggle }) => {
  // ✅ Använd utility-funktion (stödjer båda strukturer)
  const homeVisit = getBookedHomeVisit(procurementData);
  
  // Kontrollera om hembesök finns och inte har passerat
  if (!homeVisit?.meetingAt) {
    return null;
  }

  const homeVisitDate = new Date(homeVisit.meetingAt);
  const now = new Date();
  const hasHomeVisitPassed = homeVisitDate.getTime() < now.getTime();
  
  // Dölj helt om hembesöket har passerat
  if (hasHomeVisitPassed) {
    return null;
  }
  
  return (
    <div className="mb-6 bg-white rounded-lg shadow-sm border border-purple-200">
      {/* Accordion header - klickbar */}
      <div
        className="flex items-center justify-between p-4 cursor-pointer select-none bg-purple-50 hover:bg-purple-100 transition-colors"
        onClick={onToggle}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
      >
        <h2 className="text-lg font-semibold text-purple-800">
          🏠 Hembesök bokat
        </h2>
        <div className="flex items-center gap-3">
          <span className="text-sm text-purple-700 font-medium">
            {homeVisitDate.toLocaleDateString('sv-SE', {
              weekday: 'short',
              month: 'short',
              day: 'numeric',
              timeZone: 'UTC'
            })} kl {homeVisitDate.toLocaleTimeString('sv-SE', {
              hour: '2-digit',
              minute: '2-digit',
              timeZone: 'UTC'
            })}
          </span>
          <svg
            className={`w-5 h-5 text-purple-600 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {/* Accordion content - expanderbar */}
      {expanded && (
        <div className="p-4 bg-purple-50 border-t border-purple-200">
          <div className="bg-white border border-purple-200 rounded-lg p-4">
            <p className="text-sm text-purple-800 mb-2">
              <strong>Fullständigt datum och tid:</strong>
            </p>
            <div className="text-lg font-medium text-purple-900 mb-3">
              {homeVisitDate.toLocaleDateString('sv-SE', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                timeZone: 'UTC'
              })} kl {homeVisitDate.toLocaleTimeString('sv-SE', {
                hour: '2-digit',
                minute: '2-digit',
                timeZone: 'UTC'
              })}
            </div>
            {homeVisit.notes && (
              <div className="pt-3 border-t border-purple-200">
                <p className="text-sm text-purple-800 mb-1">
                  <strong>Anteckningar:</strong>
                </p>
                <p className="text-sm text-purple-700 italic">
                  "{homeVisit.notes}"
                </p>
              </div>
            )}
            <div className="mt-3 pt-3 border-t border-purple-200">
              <p className="text-xs text-purple-600">
                💡 Denna sektion försvinner automatiskt efter att hembesöket har genomförts.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookedHomeVisitSection;

